import { TmdxResourceCard } from '@features/components/ResourceCards';
import { toast } from 'react-toastify';
import { useMemo } from 'react';
import { MdOutlineWifiTethering } from 'react-icons/md';
import { NestedDropdownProps } from '@ui';
import { getCSDisabledKeys, getCSMenuItems, getSurgeonDisabledKeys, getSurgeonMenuItems } from './config';
import { EmployeeRole } from '@/api/apis/resources';
import { Uuid } from '@/api/types';
import {
  Case,
  CaseResourceType,
  EmployeeLabels,
  EmployeeBaseResourceData,
  OrganType,
  ResourceLogisticsStatus,
} from '@/api/apis/case';
import { useChangeCsStatus, useChangeSurgeonStatus, useSetAssignedCsLabel } from '@/queries/case';
import { getFullName } from '@/utils/helpers';
import { retrieveUnifiedError } from '@/api/utils';
import { csStatusName, employeeLabelColors, rsStatusName } from '@/constants/resources.constants';
import { EMPLOYEE_ROLE_NAMES } from '@/constants/user.constants';
import { EmployeeModal } from './EmployeeModal';

type LogisticResourceCardProps = {
  id: Uuid;
  employee?: EmployeeBaseResourceData;
  logisticsStatus: ResourceLogisticsStatus;
  role: EmployeeRole.ClinicalSpecialist | EmployeeRole.RetrievalSurgeon | EmployeeRole.ContractorRetrievalSurgeon;
  caseId: Uuid;
  menuDisabled: boolean;
  caseData: Case;
  shadowOrgans?: OrganType[];
  proctorOrgans?: OrganType[];
};

export const ClinicalEmployeeCard = ({
  id,
  employee,
  logisticsStatus,
  role,
  caseId,
  menuDisabled,
  caseData,
  shadowOrgans = [],
  proctorOrgans = [],
}: LogisticResourceCardProps) => {
  const { mutateAsync: mutateCsLabels } = useSetAssignedCsLabel();
  const { mutateAsync: mutateCsStatus } = useChangeCsStatus();
  const { mutateAsync: mutateSurgeonStatus } = useChangeSurgeonStatus();

  const isCs = role === EmployeeRole.ClinicalSpecialist;
  const csResourcesLabels = useMemo(() => {
    const csList = caseData.resources.filter((res) => res.resourceType === CaseResourceType.ClinicalSpecialist);
    const csLabels = csList.flatMap((res) => res.resourceData?.labels || []);

    return new Set(csLabels);
  }, [caseData.resources]);

  const menuProps = useMemo(() => {
    const isShadowAvailable = shadowOrgans.includes(caseData.organType);
    const isProctorAvailable = proctorOrgans.includes(caseData.organType);

    return {
      disabledKeys: isCs
        ? getCSDisabledKeys({
            status: logisticsStatus,
            isShadowAvailable,
            isProctorAvailable,
            currentEmployeeLabels: employee?.labels || [],
            assignedLabelsSet: csResourcesLabels,
          })
        : getSurgeonDisabledKeys(logisticsStatus),
      selectedKeys: [logisticsStatus, ...(employee?.labels || [])],
    };
  }, [caseData.organType, logisticsStatus, isCs, shadowOrgans, proctorOrgans, employee, csResourcesLabels]);

  const changeEmployeeStatus = async (
    employeeId: Uuid,
    prevStatus: ResourceLogisticsStatus,
    status: ResourceLogisticsStatus,
  ) => {
    if (prevStatus === status) return;
    try {
      const mutation = isCs ? mutateCsStatus : mutateSurgeonStatus;
      await mutation({ caseId, employeeId, status });
      toast.success('Status has been changed');
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const setCsLabels = async (currentCaseId: Uuid, employeeId: Uuid, label: EmployeeLabels) => {
    try {
      const hasSelectedLabel = employee?.labels?.includes(label);
      await mutateCsLabels({ caseId: currentCaseId, employeeId, labels: hasSelectedLabel ? [] : [label] });
      toast.success('CS label has been changed');
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  if (!employee) return null;

  const { firstName, lastName, profilePhoto } = employee;
  const getListItems = isCs ? getCSMenuItems : getSurgeonMenuItems;
  const menuItems = getListItems(
    (newStatus: ResourceLogisticsStatus) => changeEmployeeStatus(id, logisticsStatus, newStatus),
    (newLabel: EmployeeLabels) => setCsLabels(caseId, id, newLabel),
    caseData,
  );

  const getSubTitle = () => {
    if (isCs) return EMPLOYEE_ROLE_NAMES['clinical-specialist'];
    if (!isCs && role === EmployeeRole.ContractorRetrievalSurgeon) return EMPLOYEE_ROLE_NAMES[role];
    return EMPLOYEE_ROLE_NAMES['retrieval-surgeon'];
  };

  return (
    <div className="w-80">
      <EmployeeModal employeeId={id}>
        <TmdxResourceCard
          icon={<MdOutlineWifiTethering className="text-base-20" />}
          menuDisabled={menuDisabled}
          menuProps={menuProps as Partial<NestedDropdownProps>}
          list={menuItems}
          type="user"
          title={getFullName(firstName, lastName)}
          subTitle={getSubTitle()}
          phoneNumber={employee.phone}
          avatarProps={{
            src: profilePhoto,
            label: employee.labels?.[0] || undefined,
            isStatus: !!employee.labels?.[0],
            chipProps: {
              color: employee.labels?.[0] ? employeeLabelColors[employee.labels?.[0]] : undefined,
            },
          }}
          chipProps={{ status: logisticsStatus }}
          chipText={isCs ? csStatusName[logisticsStatus] : rsStatusName[logisticsStatus]}
        />
      </EmployeeModal>
    </div>
  );
};
